import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { MdAdd, MdAttachFile, MdCampaign, MdContentCopy, MdDescription, MdDoneAll, MdPlayArrow, MdSchedule, MdSearch, MdSend, MdStop, MdSync, MdVisibility } from 'react-icons/md';
import { getData, postData, postFileUpload, putData } from '../../../Webservice/Webservice';
import WhatsappForm from './WhatsappForm';
import { FONT, Progress, TEMPLATE_STATUS, WA_GREEN, WA_STATUS, WaBubble, WaChip, authToken, fmtDateTime } from './waShared';

const iconBtn = (color, bg) => ({ color, background: bg, borderRadius: '8px', '&:hover': { background: bg, filter: 'brightness(.95)' } });
const EMPTY_TEMPLATE = { category: 'MARKETING', templateName: '', language: 'en', headerType: 'NONE', headerContent: '', headerFile: null, headerPreview: '', bodyText: '', footerText: '' };
const extractVariables = (text) => [...new Set([...(text || '').matchAll(/\{\{(\d+)\}\}/g)].map((m) => m[1]))];

function Whatsapp() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);

  const [tplOpen, setTplOpen] = useState(false);
  const [tpl, setTpl] = useState(EMPTY_TEMPLATE);
  const [tplErrors, setTplErrors] = useState({});

  const loadCampaigns = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const res = await getData('whatsapp-campaign/list', null, authToken(), false);
      setCampaigns(res?.campaigns || []);
      setError('');
    } catch (e) {
      if (!quiet) setError('Failed to load WhatsApp campaigns. Please try again.');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    setSyncing(true);
    try {
      const res = await getData('whatsapp-campaign/templates', null, authToken(), false);
      setTemplates(res?.templates || []);
    } catch (e) {
      setTemplates([]);
    } finally {
      setSyncing(false);
    }
  }, []);

  useEffect(() => { loadCampaigns(); loadTemplates(); }, [loadCampaigns, loadTemplates]);

  const live = campaigns.some((c) => ['running', 'pending'].includes(c.status));
  useEffect(() => {
    if (!live || showForm) return undefined;
    const t = setInterval(() => loadCampaigns(true), 6000);
    return () => clearInterval(t);
  }, [live, showForm, loadCampaigns]);

  const act = async (kind, c) => {
    setBusy(true);
    try {
      if (kind === 'start') await putData(`whatsapp-campaign/start/${c._id}`, {}, authToken());
      else if (kind === 'stop') await putData(`whatsapp-campaign/stop/${c._id}`, {}, authToken());
      else if (kind === 'duplicate') {
        const res = await postData(`whatsapp-campaign/duplicate/${c._id}`, {}, authToken());
        if (res?.campaign?._id) return navigate(`/whatsapp/${res.campaign._id}`);
      }
      await loadCampaigns(true);
    } catch (e) {
      /* the service already showed the reason */
    } finally {
      setBusy(false);
    }
  };

  // ---------- template registration ----------
  const setTplField = (patch) => { setTpl((p) => ({ ...p, ...patch })); setTplErrors({}); };
  const tplVars = extractVariables(`${tpl.bodyText} ${tpl.headerContent}`);

  const saveTemplate = async () => {
    const errs = {};
    if (!/^[a-z0-9_]+$/.test(tpl.templateName)) errs.templateName = 'Use only lowercase letters, numbers and underscores';
    if (!tpl.bodyText.trim()) errs.bodyText = 'The message body is required';
    if (tpl.headerType === 'MEDIA' && !tpl.headerFile) errs.header = 'Upload a sample image, video or document';
    if (tpl.headerType === 'TEXT' && !tpl.headerContent.trim()) errs.header = 'Enter the header text';
    const nums = tplVars.map(Number).sort((a, b) => a - b);
    if (nums.some((n, i) => n !== i + 1)) errs.bodyText = 'Number the variables in order: {{1}}, {{2}}, {{3}}…';
    setTplErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      if (tpl.headerType === 'MEDIA' && tpl.headerFile) {
        const fd = new FormData();
        fd.append('category', tpl.category);
        fd.append('templateName', tpl.templateName);
        fd.append('language', tpl.language);
        fd.append('headerType', tpl.headerType);
        fd.append('bodyText', tpl.bodyText);
        fd.append('content', tpl.bodyText);
        fd.append('footerText', tpl.footerText);
        fd.append('variables', JSON.stringify(tplVars));
        fd.append('media', tpl.headerFile);
        await postFileUpload('whatsapp-campaign/templates', fd, authToken());
      } else {
        await postData('whatsapp-campaign/templates', { category: tpl.category, templateName: tpl.templateName, language: tpl.language, headerType: tpl.headerType, headerContent: tpl.headerContent, bodyText: tpl.bodyText, content: tpl.bodyText, footerText: tpl.footerText, variables: tplVars }, authToken());
      }
      setTplOpen(false);
      setTpl(EMPTY_TEMPLATE);
      await loadTemplates();
    } catch (e) {
      /* shown by the service */
    } finally {
      setBusy(false);
    }
  };

  const kpis = useMemo(() => {
    const sum = (k) => campaigns.reduce((a, c) => a + (c.breakdown?.[k] || 0), 0);
    return [
      { label: 'Campaigns', value: campaigns.length, icon: MdCampaign, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', filter: '' },
      { label: 'Sending now', value: campaigns.filter((c) => c.status === 'running').length, icon: MdSend, gradient: 'linear-gradient(135deg,#10b981,#059669)', filter: 'running' },
      { label: 'Scheduled', value: campaigns.filter((c) => c.status === 'scheduled').length, icon: MdSchedule, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', filter: 'scheduled' },
      { label: 'Messages delivered', value: (sum('delivered') + sum('read')).toLocaleString('en-IN'), icon: MdDoneAll, gradient: 'linear-gradient(135deg,#25D366,#128c7e)', filter: 'completed' },
    ];
  }, [campaigns]);

  const rows = campaigns.filter((c) => {
    if (statusFilter && c.status !== statusFilter) return false;
    const q = search.trim().toLowerCase();
    return !q || `${c.name} ${c.campId || ''} ${c.templateName || ''}`.toLowerCase().includes(q);
  });

  if (showForm) {
    return <WhatsappForm templates={templates} onBack={() => setShowForm(false)} onCreated={() => { setShowForm(false); loadCampaigns(); }} />;
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, background: '#f8fafc', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 25, color: '#0f172a' }}>WhatsApp Campaigns</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>Send approved template messages and track delivery and reads.</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button startIcon={<MdDescription />} onClick={() => { setTpl(EMPTY_TEMPLATE); setTplErrors({}); setTplOpen(true); }} sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 2.5, color: '#0f172a', background: '#fff', border: '1px solid #cbd5e1', '&:hover': { background: '#f1f5f9' } }}>Add Template</Button>
            <Button variant="contained" startIcon={<MdAdd />} onClick={() => setShowForm(true)} sx={{ fontFamily: FONT, background: 'rgb(255 209 2)', color: 'black', textTransform: 'none', borderRadius: '10px', fontWeight: 700, px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', border: '1px solid black', '&:hover': { background: 'rgb(255 209 2)', transform: 'translateY(-1px)' } }}>New Campaign</Button>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2, mb: 3 }}>
          {kpis.map((k) => (
            <Paper key={k.label} elevation={0} onClick={() => { setTab(0); setStatusFilter(k.filter); }} sx={{ p: 2.5, borderRadius: '16px', color: '#fff', background: k.gradient, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', outline: statusFilter === k.filter && k.filter ? '3px solid #0f172a' : 'none', outlineOffset: 2, boxShadow: '0 4px 12px rgba(15,23,42,.1)', transition: 'transform .15s', '&:hover': { transform: 'translateY(-2px)' } }}>
              <Box sx={{ background: 'rgba(255,255,255,.22)', borderRadius: '14px', p: 1.5, display: 'flex' }}><k.icon size={26} /></Box>
              <Box>
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, opacity: 0.92, fontFamily: FONT }}>{k.label}</Typography>
                <Typography sx={{ fontSize: 25, fontWeight: 800, lineHeight: 1.2, fontFamily: FONT }}>{k.value}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, '& .MuiTab-root': { fontFamily: FONT, fontWeight: 800, textTransform: 'none', fontSize: 13.5 }, '& .MuiTabs-indicator': { background: WA_GREEN, height: 3 } }}>
          <Tab label={`Campaigns (${campaigns.length})`} />
          <Tab label={`Templates (${templates.length})`} />
        </Tabs>

        {tab === 0 ? (
          <>
            <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '14px', border: '1px solid #e2e8f0', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField size="small" placeholder="Search name, campaign ID or template" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 240 }} InputProps={{ startAdornment: <MdSearch size={20} style={{ marginRight: 8, color: '#94a3b8' }} /> }} />
              <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 160 }}>
                <MenuItem value="">All</MenuItem>
                {Object.entries(WA_STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
              </TextField>
              {(search || statusFilter) && <Button onClick={() => { setSearch(''); setStatusFilter(''); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Clear</Button>}
            </Paper>

            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(15,23,42,.05)' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ '& th': { backgroundColor: '#f8fafc', fontWeight: 800, py: 1.75, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', fontFamily: FONT, borderBottom: '2px solid #e2e8f0' } }}>
                    <TableCell>Campaign</TableCell>
                    <TableCell>Template</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Delivered</TableCell>
                    <TableCell>Read</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => <TableRow key={i}>{[200, 140, 150, 60, 60, 90, 130].map((w, j) => <TableCell key={j}><Skeleton width={w} /></TableCell>)}</TableRow>)
                  ) : rows.length === 0 ? (
                    <TableRow><TableCell colSpan={7} sx={{ py: 6, textAlign: 'center' }}><Typography sx={{ fontFamily: FONT, color: '#64748b', fontWeight: 600 }}>{campaigns.length ? 'No campaigns match your filters.' : 'No WhatsApp campaigns yet. Create your first one.'}</Typography></TableCell></TableRow>
                  ) : rows.map((c) => {
                    const b = c.breakdown || {};
                    const reached = (b.sent || 0) + (b.delivered || 0) + (b.read || 0);
                    const delivered = (b.delivered || 0) + (b.read || 0);
                    const canStart = ['draft', 'scheduled', 'pending'].includes(c.status);
                    const canStop = ['draft', 'scheduled', 'pending', 'running'].includes(c.status);
                    return (
                      <TableRow key={c._id} hover onClick={() => navigate(`/whatsapp/${c._id}`)} sx={{ cursor: 'pointer', '& td': { borderBottom: '1px solid #f1f5f9', py: 1.5, fontFamily: FONT } }}>
                        <TableCell>
                          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13, color: '#0f172a' }}>{c.name}</Typography>
                          <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#64748b' }}>{c.campId ? `${c.campId} · ` : ''}{c.status === 'scheduled' ? `Goes out ${fmtDateTime(c.scheduledAt)}` : `Created ${fmtDateTime(c.createdAt)}`}</Typography>
                        </TableCell>
                        <TableCell><Chip size="small" label={c.templateName || 'N/A'} sx={{ fontFamily: FONT, fontWeight: 600, background: '#f1f5f9', color: '#475569' }} /></TableCell>
                        <TableCell><Progress stats={{ ...c.stats, sent: reached, skipped: 0 }} status={c.status} /></TableCell>
                        <TableCell><Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 12.5 }}>{reached ? `${Math.round((delivered / reached) * 100)}%` : '-'}</Typography></TableCell>
                        <TableCell><Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 12.5 }}>{reached ? `${Math.round(((b.read || 0) / reached) * 100)}%` : '-'}</Typography></TableCell>
                        <TableCell><WaChip status={c.status} /></TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                          {canStart && <Tooltip title="Start now" arrow><span><IconButton size="small" disabled={busy} onClick={() => act('start', c)} sx={{ ...iconBtn('#166534', '#dcfce7'), mr: 0.75 }}><MdPlayArrow size={18} /></IconButton></span></Tooltip>}
                          {canStop && <Tooltip title="Stop" arrow><span><IconButton size="small" disabled={busy} onClick={() => act('stop', c)} sx={{ ...iconBtn('#b91c1c', '#fee2e2'), mr: 0.75 }}><MdStop size={18} /></IconButton></span></Tooltip>}
                          <Tooltip title="Duplicate as draft" arrow><span><IconButton size="small" disabled={busy} onClick={() => act('duplicate', c)} sx={{ ...iconBtn('#0369a1', '#e0f2fe'), mr: 0.75 }}><MdContentCopy size={16} /></IconButton></span></Tooltip>
                          <Tooltip title="Track" arrow><IconButton size="small" onClick={() => navigate(`/whatsapp/${c._id}`)} sx={iconBtn('#4f46e5', 'rgba(79,70,229,.1)')}><MdVisibility size={17} /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button startIcon={syncing ? <CircularProgress size={16} /> : <MdSync />} onClick={loadTemplates} disabled={syncing} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', color: '#128c7e', background: 'rgba(37,211,102,.12)', px: 2 }}>{syncing ? 'Syncing…' : 'Sync from Meta'}</Button>
            </Box>
            <Grid container spacing={2.5}>
              {templates.map((t) => (
                <Grid item xs={12} md={6} xl={4} key={t._id}>
                  <Paper elevation={0} sx={{ p: 2.5, height: '100%', borderRadius: '16px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 1.5, boxShadow: '0 4px 16px rgba(15,23,42,.05)' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 12.5, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis' }} title={t.name}>{t.name}</Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#64748b' }}>{t.language}{t.headerType && t.headerType !== 'NONE' ? ` · ${t.headerType.toLowerCase()} header` : ''}</Typography>
                      </Box>
                      <WaChip status={t.status} map={TEMPLATE_STATUS} />
                    </Box>
                    <WaBubble headerType={t.headerType} body={t.content} empty="No body text" />
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                      {(t.variables || []).length ? t.variables.map((v) => <Chip key={v} label={`{{${v}}}`} size="small" sx={{ fontFamily: 'monospace', fontSize: 11.5, background: '#ecfdf5', color: '#065f46' }} />) : <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8' }}>No variables</Typography>}
                    </Box>
                  </Paper>
                </Grid>
              ))}
              <Grid item xs={12} md={6} xl={4}>
                <Box onClick={() => { setTpl(EMPTY_TEMPLATE); setTplErrors({}); setTplOpen(true); }} sx={{ height: '100%', minHeight: 200, border: '2px dashed #cbd5e1', borderRadius: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, cursor: 'pointer', color: '#475569', '&:hover': { borderColor: WA_GREEN, background: 'rgba(37,211,102,.06)' } }}>
                  <MdAdd size={30} />
                  <Typography sx={{ fontFamily: FONT, fontWeight: 800 }}>Submit a new template to Meta</Typography>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
      </Box>

      {/* register a template with Meta */}
      <Dialog open={tplOpen} onClose={() => !busy && setTplOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, background: 'linear-gradient(135deg, #25D3661f, #25D36608)', borderBottom: `3px solid ${WA_GREEN}` }}>
          <Box sx={{ background: WA_GREEN, color: '#fff', borderRadius: '12px', p: 1.1, display: 'flex' }}><MdDescription size={22} /></Box>
          <Box>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, color: '#0f172a', lineHeight: 1.2 }}>New WhatsApp template</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>It is sent to Meta for review. You can use it in campaigns once it shows as Approved.</Typography>
          </Box>
        </Box>
        <DialogContent sx={{ pt: '24px !important' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.35fr 1fr' }, gap: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
              <TextField fullWidth size="small" label="Template name" required placeholder="order_shipped_update" value={tpl.templateName} onChange={(e) => setTplField({ templateName: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} error={!!tplErrors.templateName} helperText={tplErrors.templateName || 'Lowercase letters, numbers and underscores only. It cannot be changed later.'} inputProps={{ style: { fontFamily: 'monospace' } }} />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField select fullWidth size="small" label="Category" value={tpl.category} onChange={(e) => setTplField({ category: e.target.value })}>
                  <MenuItem value="MARKETING">Marketing</MenuItem>
                  <MenuItem value="UTILITY">Utility</MenuItem>
                  <MenuItem value="AUTHENTICATION">Authentication</MenuItem>
                </TextField>
                <TextField select fullWidth size="small" label="Language" value={tpl.language} onChange={(e) => setTplField({ language: e.target.value })}>
                  <MenuItem value="en">English (en)</MenuItem>
                  <MenuItem value="en_US">English (US)</MenuItem>
                  <MenuItem value="hi">Hindi (hi)</MenuItem>
                  <MenuItem value="ta">Tamil (ta)</MenuItem>
                </TextField>
              </Box>
              <TextField select fullWidth size="small" label="Header" value={tpl.headerType} onChange={(e) => setTplField({ headerType: e.target.value, headerContent: '', headerFile: null, headerPreview: '' })}>
                <MenuItem value="NONE">No header</MenuItem>
                <MenuItem value="TEXT">Text</MenuItem>
                <MenuItem value="MEDIA">Image, video or document</MenuItem>
              </TextField>
              {tpl.headerType === 'TEXT' && <TextField fullWidth size="small" label="Header text" value={tpl.headerContent} onChange={(e) => setTplField({ headerContent: e.target.value })} error={!!tplErrors.header} helperText={tplErrors.header} />}
              {tpl.headerType === 'MEDIA' && (
                <Box>
                  <Button component="label" startIcon={<MdAttachFile />} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', background: '#f1f5f9', color: '#0f172a', px: 2 }}>
                    {tpl.headerFile ? tpl.headerFile.name : 'Upload a sample file'}
                    <input type="file" hidden accept="image/*,video/*,application/pdf" onChange={(e) => { const f = e.target.files?.[0]; if (f) setTplField({ headerFile: f, headerPreview: f.type.startsWith('image') ? URL.createObjectURL(f) : '' }); }} />
                  </Button>
                  {tplErrors.header && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#dc2626', mt: 0.5 }}>{tplErrors.header}</Typography>}
                </Box>
              )}
              <TextField fullWidth size="small" label="Message body" required multiline minRows={5} placeholder="Hello {{1}}, your order {{2}} has shipped." value={tpl.bodyText} onChange={(e) => setTplField({ bodyText: e.target.value })} error={!!tplErrors.bodyText} helperText={tplErrors.bodyText || 'Use {{1}}, {{2}}… for the parts that change per person.'} />
              <TextField fullWidth size="small" label="Footer (optional)" value={tpl.footerText} onChange={(e) => setTplField({ footerText: e.target.value })} />
              {tplVars.length > 0 && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>Variables found: {tplVars.map((v) => `{{${v}}}`).join(', ')}</Typography>}
            </Box>
            <Box>
              <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75 }}>Preview</Typography>
              <WaBubble headerType={tpl.headerType === 'MEDIA' ? 'IMAGE' : undefined} headerImage={tpl.headerPreview} header={tpl.headerType === 'TEXT' ? tpl.headerContent : ''} body={tpl.bodyText.replace(/\{\{(\d+)\}\}/g, '[$1]')} footer={tpl.footerText} empty="Your message appears here as you type" />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setTplOpen(false)} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569', borderRadius: '10px', px: 2.5 }}>Cancel</Button>
          <Button onClick={saveTemplate} variant="contained" disabled={busy} sx={{ textTransform: 'none', fontWeight: 800, background: WA_GREEN, color: '#fff', borderRadius: '10px', px: 3.5, boxShadow: '0 4px 14px rgba(37,211,102,.39)', '&:hover': { background: '#1ebe57' } }}>{busy ? 'Submitting…' : 'Submit to Meta'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Whatsapp;
